import React from "react";
import Marquee from "react-fast-marquee";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, MapPin, Ruler, Handshake, Landmark, DollarSign, CalendarClock, Ban, Tag, BadgeDollarSign, Users } from "lucide-react";
import { Reveal } from "./Reveal";
import { LeadForm } from "./LeadForm";
import { LOTS, REVIEWS, DIFFERENTIATORS, TRUST_POINTS, WHYUS_IMAGE, BUSINESS } from "../data";
import { scrollToId } from "../hooks/useSmoothScroll";

const MARQUEE = ["Direct Cash Offers", "No Realtor Commissions", "Close On Your Timeline", "Off-Market Land", "Nationwide Land Buyers"];

export const TrustStrip = () => {
  const icons = [Ban, DollarSign, CalendarClock];
  return (
    <section className="bg-forest-700 border-b border-white/10 py-10 md:py-12 px-5 md:px-10" data-testid="trust-strip">
      <div className="max-w-7xl mx-auto grid sm:grid-cols-3 gap-6 md:gap-10">
        {TRUST_POINTS.map((t, i) => {
          const Icon = icons[i];
          return (
            <Reveal key={t.title} delay={i * 0.1}>
              <div className="flex items-start gap-4">
                <div className="w-11 h-11 shrink-0 bg-terracotta/15 border border-terracotta/30 flex items-center justify-center">
                  <Icon className="text-terracotta" size={20} />
                </div>
                <div>
                  <h3 className="text-sand font-medium text-lg">{t.title}</h3>
                  <p className="text-sand/60 text-sm leading-relaxed mt-0.5">{t.desc}</p>
                </div>
              </div>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
};

export const EditorialMarquee = () => (
  <div className="bg-terracotta py-5 border-y border-terracotta-dark" data-testid="marquee">
    <Marquee speed={38} gradient={false}>
      {[...MARQUEE, ...MARQUEE].map((t, i) => (
        <span key={i} className="mx-8 font-display text-2xl md:text-3xl text-white/95 italic">
          {t} <span className="text-white/40 not-italic ml-8">✦</span>
        </span>
      ))}
    </Marquee>
  </div>
);

export const BuyersSellers = () => {
  const pillars = [
    {
      icon: Landmark, tag: "For Land Owners", title: "Sell your land, hassle-free",
      desc: "This is what we do best. Landowners who want out of a vacant lot get a fair, no-obligation cash offer — no commissions, no listing, no waiting. We buy directly and close on your timeline.",
      cta: "Get Your Cash Offer", action: () => scrollToId("contact"), test: "pillar-sellers", primary: true,
    },
    {
      icon: Users, tag: "For Buyers, Builders & Investors", title: "Find the exact lot you need",
      desc: "We also help serious buyers who want a specific vacant lot without the search. Tell us your criteria — location, size, budget — and we'll bring you off-market land that's ready to close.",
      cta: "Browse Available Lots", action: () => scrollToId("lots"), test: "pillar-buyers", primary: false,
    },
  ];
  return (
    <section id="about" className="bg-sand py-24 md:py-32 px-5 md:px-10" data-testid="section-about">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <p className="text-terracotta text-xs uppercase tracking-[0.3em] font-semibold mb-4">What We Do</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-forest max-w-3xl leading-[1.02]">
            We buy vacant land directly from owners — and help buyers too.
          </h2>
        </Reveal>

        <div className="grid md:grid-cols-2 gap-6 md:gap-8 mt-14">
          {pillars.map((p, i) => (
            <Reveal key={p.tag} delay={i * 0.12}>
              <div className={`group h-full border p-9 md:p-11 hover:-translate-y-2 hover:shadow-2xl transition-all duration-500 flex flex-col ${p.primary ? "bg-forest border-forest text-sand" : "bg-white border-forest/10"}`}>
                <div className={`w-14 h-14 flex items-center justify-center mb-7 ${p.primary ? "bg-terracotta" : "bg-forest"}`}>
                  <p.icon className="text-sand" size={24} />
                </div>
                <p className="text-terracotta text-xs uppercase tracking-[0.25em] font-semibold mb-3">{p.tag}</p>
                <h3 className={`font-display text-3xl mb-4 ${p.primary ? "text-sand" : "text-forest"}`}>{p.title}</h3>
                <p className={`leading-relaxed mb-8 flex-1 ${p.primary ? "text-sand/75" : "text-forest/70"}`}>{p.desc}</p>
                <button
                  data-testid={p.test}
                  onClick={p.action}
                  className={`group/btn inline-flex items-center gap-2 font-medium border-b-2 border-terracotta pb-1 w-fit hover:gap-3 transition-all duration-300 ${p.primary ? "text-sand" : "text-forest"}`}
                >
                  {p.cta} <ArrowRight size={17} className="text-terracotta" />
                </button>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const WhyUs = () => (
  <section className="bg-forest py-24 md:py-32 px-5 md:px-10" data-testid="section-whyus">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
      <Reveal>
        <div className="relative">
          <div className="overflow-hidden">
            <motion.img
              src={WHYUS_IMAGE} alt="Vacant land field under open sky" loading="lazy" decoding="async"
              className="w-full h-[420px] md:h-[520px] object-cover"
              initial={{ scale: 1.15 }} whileInView={{ scale: 1 }} viewport={{ once: true }} transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
            />
          </div>
          <div className="absolute -bottom-6 -right-2 md:right-6 bg-terracotta text-white px-7 py-6 max-w-[240px]">
            <p className="font-display text-3xl leading-tight">Direct cash buyer</p>
            <p className="text-xs uppercase tracking-[0.2em] mt-2 text-white/80">No fees · No commissions</p>
          </div>
        </div>
      </Reveal>

      <div>
        <Reveal>
          <p className="text-terracotta text-xs uppercase tracking-[0.3em] font-semibold mb-4">Why Urbina</p>
          <h2 className="font-display text-4xl md:text-5xl text-sand leading-[1.02] mb-10">
            We handle the offer, the paperwork, and the close.
          </h2>
        </Reveal>
        <div className="grid sm:grid-cols-2 gap-x-8 gap-y-8">
          {DIFFERENTIATORS.map((d, i) => {
            const Icon = [Ban, BadgeDollarSign, Tag, Handshake][i];
            return (
              <Reveal key={d.title} delay={i * 0.08}>
                <div className="flex gap-4">
                  <Icon className="text-terracotta shrink-0 mt-1" size={22} />
                  <div>
                    <h4 className="text-sand font-medium text-lg mb-1">{d.title}</h4>
                    <p className="text-sand/60 text-sm leading-relaxed">{d.desc}</p>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
        <Reveal delay={0.2}>
          <button
            data-testid="whyus-cta"
            onClick={() => scrollToId("reviews")}
            className="group mt-11 inline-flex items-center gap-2 bg-sand text-forest px-8 py-4 tracking-wide hover:bg-sand-200 transition-colors duration-300"
          >
            See Client Reviews <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
          </button>
        </Reveal>
      </div>
    </div>
  </section>
);

export const HowItWorks = () => {
  const steps = [
    { n: "01", t: "Tell us about your land", d: "Share the location, size, and details. It takes a couple of minutes and there's no obligation." },
    { n: "02", t: "Get a fair cash offer", d: "We review the parcel and present a direct, no-commission cash offer — clearly explained." },
    { n: "03", t: "Close on your timeline", d: "Accept and we handle the paperwork. You pick the closing date that works for you." },
  ];
  return (
    <section id="how" className="bg-sand-200 py-24 md:py-32 px-5 md:px-10" data-testid="section-how">
      <div className="max-w-7xl mx-auto">
        <Reveal>
          <p className="text-terracotta text-xs uppercase tracking-[0.3em] font-semibold mb-4">How It Works</p>
          <h2 className="font-display text-4xl md:text-5xl text-forest max-w-2xl leading-[1.02]">Three simple steps from offer to closing.</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-8 md:gap-4 mt-16">
          {steps.map((s, i) => (
            <Reveal key={s.n} delay={i * 0.12}>
              <div className="border-t-2 border-forest/15 pt-7 md:pr-8">
                <p className="font-mono text-terracotta text-sm mb-6">{s.n}</p>
                <h3 className="font-display text-3xl text-forest mb-3">{s.t}</h3>
                <p className="text-forest/70 leading-relaxed">{s.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export const AvailableLots = () => (
  <section id="lots" className="bg-sand py-24 md:py-32 px-5 md:px-10" data-testid="section-lots">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <p className="text-terracotta text-xs uppercase tracking-[0.3em] font-semibold mb-4">Available Lots</p>
            <h2 className="font-display text-4xl md:text-5xl text-forest max-w-xl leading-[1.02]">Off-market land, added as we acquire it.</h2>
          </div>
          <button data-testid="lots-cta" onClick={() => scrollToId("contact")} className="group inline-flex items-center gap-2 text-forest font-medium border-b-2 border-terracotta pb-1 hover:gap-3 transition-all duration-300">
            Request current inventory <ArrowUpRight size={18} className="text-terracotta" />
          </button>
        </div>
      </Reveal>

      {LOTS.length === 0 ? (
        <Reveal>
          <div className="bg-white border border-forest/10 px-8 py-16 md:py-20 text-center" data-testid="lots-empty-state">
            <div className="w-14 h-14 bg-forest mx-auto flex items-center justify-center mb-7">
              <Landmark className="text-sand" size={24} />
            </div>
            <h3 className="font-display text-3xl md:text-4xl text-forest mb-4">New parcels are on the way</h3>
            <p className="text-forest/70 max-w-lg mx-auto mb-8 leading-relaxed">
              We source off-market land nationwide and match it directly to buyers. Tell us exactly what you're looking for — location, size, and budget — and we'll bring you lots that fit before they ever hit the market.
            </p>
            <button data-testid="lots-empty-cta" onClick={() => scrollToId("contact")} className="group inline-flex items-center gap-2 bg-terracotta text-white px-8 py-4 tracking-wide hover:bg-terracotta-dark transition-colors duration-300">
              Tell Us What You're Looking For <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
            </button>
          </div>
        </Reveal>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
          {LOTS.map((lot, i) => (
            <Reveal key={lot.id} delay={(i % 3) * 0.1}>
              <div className="group bg-white border border-forest/10 overflow-hidden hover:-translate-y-2 hover:shadow-2xl transition-all duration-500" data-testid={`lot-card-${lot.id}`}>
                <div className="relative h-56 overflow-hidden">
                  <img src={lot.image} alt={lot.title} loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <span className="absolute bottom-4 right-4 bg-terracotta text-white font-display text-xl px-4 py-1.5">{lot.price}</span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl text-forest mb-3">{lot.title}</h3>
                  <div className="flex items-center gap-5 text-forest/60 text-sm">
                    <span className="flex items-center gap-1.5"><MapPin size={15} className="text-terracotta" /> {lot.location}</span>
                    <span className="flex items-center gap-1.5"><Ruler size={15} className="text-terracotta" /> {lot.size}</span>
                  </div>
                  <button onClick={() => scrollToId("contact")} data-testid={`lot-inquire-${lot.id}`} className="mt-5 w-full border border-forest/20 text-forest py-3 text-sm tracking-wide hover:bg-forest hover:text-sand transition-colors duration-300">
                    Inquire About This Lot
                  </button>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  </section>
);

export const Reviews = () => (
  <section id="reviews" className="bg-forest-700 py-24 md:py-32 px-5 md:px-10" data-testid="section-reviews">
    <div className="max-w-7xl mx-auto">
      <Reveal>
        <p className="text-terracotta text-xs uppercase tracking-[0.3em] font-semibold mb-4">Reviews</p>
        <h2 className="font-display text-4xl md:text-5xl text-sand max-w-2xl leading-[1.02]">Built on direct, honest dealings.</h2>
      </Reveal>

      {REVIEWS.length === 0 ? (
        <Reveal delay={0.1}>
          <div className="mt-12 grid lg:grid-cols-2 gap-10 items-center bg-forest border border-white/10 p-9 md:p-12" data-testid="reviews-empty-state">
            <div>
              <p className="text-sand/75 text-lg leading-relaxed">
                Every deal we do is owner-to-buyer with no commissions, no listing fees, and a close on your timeline. As we complete transactions, verified client reviews will be featured right here.
              </p>
              <p className="text-sand/50 mt-4">Recently worked with us? We'd genuinely value your feedback.</p>
            </div>
            <div className="lg:justify-self-end">
              <button data-testid="reviews-cta" onClick={() => scrollToId("contact")} className="group inline-flex items-center gap-2 bg-sand text-forest px-8 py-4 tracking-wide hover:bg-sand-200 transition-colors duration-300">
                Get in Touch <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </div>
          </div>
        </Reveal>
      ) : (
        <div className="grid md:grid-cols-3 gap-6 md:gap-8 mt-14">
          {REVIEWS.map((r, i) => (
            <Reveal key={i} delay={i * 0.12}>
              <div className="h-full bg-forest border border-white/10 p-8 flex flex-col" data-testid={`review-${i}`}>
                <p className="font-display text-5xl text-terracotta leading-none mb-4">“</p>
                <p className="text-sand/80 leading-relaxed flex-1">{r.quote}</p>
                <div className="mt-7 pt-5 border-t border-white/10">
                  <p className="text-sand font-medium">{r.name}</p>
                  <p className="text-sand/50 text-sm">{r.role}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      )}
    </div>
  </section>
);

export const ContactSection = () => (
  <section id="contact" className="bg-sand py-24 md:py-32 px-5 md:px-10" data-testid="section-contact">
    <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-14 items-center">
      <div>
        <Reveal>
          <p className="text-terracotta text-xs uppercase tracking-[0.3em] font-semibold mb-4">Contact</p>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-forest leading-[1.0] mb-6">
            Get your cash offer.
          </h2>
          <p className="text-forest/70 text-lg leading-relaxed max-w-md mb-10">
            Selling vacant land? Tell us about your parcel and we'll send a fair, no-obligation cash offer. Buyers and investors — reach out and we'll match you with off-market land.
          </p>
        </Reveal>
        <div className="space-y-5">
          {[
            { label: "Call us", value: BUSINESS.phone, href: BUSINESS.phoneHref },
            { label: "Email", value: BUSINESS.email, href: `mailto:${BUSINESS.email}` },
            { label: "Service Area", value: BUSINESS.area, href: null },
          ].map((c) => (
            <Reveal key={c.label}>
              <div className="border-l-2 border-terracotta pl-5">
                <p className="text-xs uppercase tracking-[0.2em] text-forest/50">{c.label}</p>
                {c.href ? (
                  <a href={c.href} className="font-display text-2xl text-forest hover:text-terracotta transition-colors duration-300 break-words">{c.value}</a>
                ) : (
                  <p className="font-display text-2xl text-forest">{c.value}</p>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </div>
      <Reveal delay={0.15}>
        <LeadForm variant="solid" title="Send us a message" subtitle="We'll get right back to you." />
      </Reveal>
    </div>
  </section>
);

export const FinalCTA = () => (
  <section className="relative bg-forest py-28 md:py-40 px-5 md:px-10 overflow-hidden" data-testid="section-final-cta">
    <div className="absolute inset-0 opacity-20" style={{ background: "radial-gradient(circle at 70% 30%, #C25E4A 0%, transparent 55%)" }} />
    <div className="relative max-w-4xl mx-auto text-center">
      <Reveal>
        <p className="text-terracotta text-xs uppercase tracking-[0.3em] font-semibold mb-6">Nationwide Land Buyers</p>
        <h2 className="font-display text-4xl md:text-6xl lg:text-7xl text-sand leading-[0.98] mb-8">
          Ready to sell your <span className="text-terracotta italic">vacant land?</span>
        </h2>
        <p className="text-sand/70 text-lg max-w-xl mx-auto mb-11">
          Get a fair cash offer with no commissions and no listing fees — anywhere we buy across the U.S. One conversation is all it takes.
        </p>
        <button
          data-testid="final-cta-btn"
          onClick={() => scrollToId("contact")}
          className="group inline-flex items-center gap-2 bg-terracotta text-white px-10 py-5 text-lg tracking-wide hover:bg-terracotta-dark transition-colors duration-300"
        >
          Get a Cash Offer <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform duration-300" />
        </button>
      </Reveal>
    </div>
  </section>
);
